/**
 * MC Planner's own blocks, for the demo island when no game file is loaded.
 *
 * Every texture here is drawn in code: original pixel art in the 16 × 16 block style, nothing taken or traced from
 * Minecraft's files (CLAUDE.md: Mojang's assets are never shipped). The block states and models use the vanilla
 * *format*, so the normal model baker and chunk mesher draw these blocks exactly like a jar's: face shading, smooth
 * ambient occlusion, biome tints, cut-out leaves, see-through water. lib/demoWorld.ts uses vanilla block names, so
 * with the player's own game file loaded the same island shows the game's real textures instead.
 *
 * Pure functions (no DOM): runs on the main thread, in workers and in Node tests.
 */
import { textureArrayFrom, type AssetBundle, type Rgba } from "@mcp/assets";

const S = 16;
type RGB = readonly [number, number, number];

const rgb = (h: number): RGB => [(h >> 16) & 255, (h >> 8) & 255, h & 255];
const grey = (v: number): RGB => [v, v, v];
const shade = (c: RGB, k: number): RGB => [Math.min(255, Math.round(c[0] * k)), Math.min(255, Math.round(c[1] * k)), Math.min(255, Math.round(c[2] * k))];
const mixc = (a: RGB, b: RGB, t: number): RGB => [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t), Math.round(a[2] + (b[2] - a[2]) * t)];

function mulberry(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A 16 × 16 RGBA tile; coordinates wrap, so everything drawn on it tiles seamlessly. */
class Tile {
  readonly data = new Uint8Array(S * S * 4);
  private o(x: number, y: number) {
    return ((((y % S) + S) % S) * S + (((x % S) + S) % S)) * 4;
  }
  set(x: number, y: number, c: RGB, a = 255) {
    const o = this.o(x, y);
    this.data[o] = c[0]; this.data[o + 1] = c[1]; this.data[o + 2] = c[2]; this.data[o + 3] = a;
  }
  get(x: number, y: number): RGB {
    const o = this.o(x, y);
    return [this.data[o], this.data[o + 1], this.data[o + 2]];
  }
  opaque(x: number, y: number) {
    return this.data[this.o(x, y) + 3] > 0;
  }
  clear(x: number, y: number) {
    this.data[this.o(x, y) + 3] = 0;
  }
  each(fn: (x: number, y: number) => RGB | null) {
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) { const c = fn(x, y); if (c) this.set(x, y, c); else this.clear(x, y); }
    return this;
  }
  copy() {
    const t = new Tile();
    t.data.set(this.data);
    return t;
  }
  rgba(): Rgba {
    return { width: S, height: S, data: this.data };
  }
}

/** Tileable value noise on the tile, `cells` lattice cells per side. */
function tileNoise(seed: number, cells: number) {
  const r = mulberry(seed);
  const g = Array.from({ length: cells * cells }, () => r());
  const at = (i: number, j: number) => g[(((j % cells) + cells) % cells) * cells + (((i % cells) + cells) % cells)];
  return (x: number, y: number) => {
    const fx = ((x + 0.5) / S) * cells - 0.5, fy = ((y + 0.5) / S) * cells - 0.5;
    const i = Math.floor(fx), j = Math.floor(fy);
    let u = fx - i, v = fy - j;
    u = u * u * (3 - 2 * u); v = v * v * (3 - 2 * v);
    const a = at(i, j), b = at(i + 1, j), c = at(i, j + 1), d = at(i + 1, j + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
}

/**
 * A field of 256 values in [0, 1), evenly spread (ranked), from noise octaves [cells, weight] plus per-pixel jitter.
 * Even spreading means a palette of n shades gets about 256 / n pixels each, whatever the noise did.
 */
function field(seed: number, octaves: [number, number][], jitter = 0, warp?: (x: number, y: number) => number): Float32Array {
  const ns = octaves.map(([cells], i) => tileNoise(seed * 31 + i * 7 + 1, cells));
  const r = mulberry(seed * 977 + 13);
  const raw = new Float32Array(S * S);
  for (let y = 0; y < S; y++)
    for (let x = 0; x < S; x++) {
      let v = 0;
      octaves.forEach(([, w], i) => (v += ns[i](x, y) * w));
      v += r() * jitter;
      if (warp) v += warp(x, y);
      raw[y * S + x] = v;
    }
  const order = [...raw.keys()].sort((a, b) => raw[a] - raw[b]);
  const out = new Float32Array(S * S);
  order.forEach((i, k) => (out[i] = k / (S * S)));
  return out;
}

const pick = <T,>(pal: readonly T[], v: number): T => pal[Math.max(0, Math.min(pal.length - 1, Math.floor(v * pal.length)))];

/** Fill a tile from a field and a palette ordered dark → light. */
function paint(f: Float32Array, pal: readonly RGB[], t = new Tile()) {
  return t.each((x, y) => pick(pal, f[y * S + x]));
}

const greys = (...v: number[]) => v.map(grey);
const hexes = (...v: number[]) => v.map(rgb);

// ---------------------------------------------------------------- stone & earth

function speckle(t: Tile, seed: number, n: number, len: [number, number], dark: RGB, light: RGB | null) {
  const r = mulberry(seed);
  for (let i = 0; i < n; i++) {
    const x = Math.floor(r() * S), y = Math.floor(r() * S), l = len[0] + Math.floor(r() * (len[1] - len[0] + 1));
    for (let k = 0; k < l; k++) t.set(x + k, y, dark);
    if (light && r() < 0.6) t.set(x + Math.floor(r() * l), y - 1, light);
  }
  return t;
}

function stone(seed = 1) {
  const t = paint(field(seed, [[4, 0.55], [8, 0.3]], 0.35), greys(0x68, 0x70, 0x76, 0x7b, 0x7b, 0x80, 0x80, 0x86, 0x8c, 0x95));
  return speckle(t, seed + 5, 7, [2, 4], grey(0x5f), grey(0x9a));
}

function granite() {
  const t = paint(field(41, [[4, 0.4], [8, 0.3]], 0.6), hexes(0x7e5446, 0x8a5c4c, 0x956553, 0x9d6c5a, 0xa77561, 0xb07f6c, 0xbd8f7b));
  return speckle(t, 44, 10, [1, 2], rgb(0x6b463a), rgb(0xc89a86));
}

function diorite() {
  const t = paint(field(51, [[8, 0.5]], 0.8), hexes(0x7d7d80, 0x949497, 0xa9a9ac, 0xbcbcbe, 0xbcbcbe, 0xcacacb, 0xd6d6d7, 0xe4e4e4));
  return speckle(t, 54, 12, [1, 2], grey(0x6a), null);
}

function andesite() {
  return speckle(paint(field(61, [[4, 0.5], [16, 0.3]], 0.4), greys(0x6c, 0x74, 0x7b, 0x80, 0x86, 0x8b, 0x92, 0x9b)), 64, 8, [1, 3], grey(0x62), grey(0xa2));
}

function ore(seed: number, pal: RGB[]) {
  const t = stone(seed);
  const r = mulberry(seed + 99);
  const blobs = [[3, 3], [10, 5], [5, 11], [12, 12]];
  for (const [bx, by] of blobs) {
    const n = 3 + Math.floor(r() * 3);
    for (let i = 0; i < n; i++) {
      const x = bx + Math.floor(r() * 3) - 1, y = by + Math.floor(r() * 3) - 1;
      t.set(x, y, pal[1]);
      if (r() < 0.6) t.set(x + 1, y, pal[0]);
      if (r() < 0.4) t.set(x, y - 1, pal[2]);
    }
  }
  return t;
}

function dirt(seed = 7) {
  const t = paint(field(seed, [[4, 0.4], [8, 0.4]], 0.5), hexes(0x664631, 0x714e36, 0x7b563b, 0x855e40, 0x855e40, 0x8f6746, 0x98704c, 0xa37954));
  const r = mulberry(seed + 3);
  for (let i = 0; i < 6; i++) { const x = Math.floor(r() * S), y = Math.floor(r() * S); t.set(x, y, rgb(0x9b8a78)); if (r() < 0.5) t.set(x + 1, y, rgb(0x6f6355)); }
  return t;
}

const GRASS_TOP = greys(0x9c, 0xa6, 0xae, 0xb4, 0xb4, 0xba, 0xc0, 0xc8, 0xd2);

function grassTop() {
  return paint(field(17, [[8, 0.3], [16, 0.2]], 0.9), GRASS_TOP);
}

/** grey fringe of the grass side (tinted by the biome), transparent below */
function grassSideOverlay() {
  const f = field(19, [[16, 0.4]], 0.8);
  const r = mulberry(23);
  const depth = Array.from({ length: S }, () => 4 + (r() < 0.6 ? 1 : 0) + (r() < 0.3 ? 1 : 0) + (r() < 0.12 ? 2 : 0));
  return new Tile().each((x, y) => (y < depth[x] ? pick(GRASS_TOP, f[y * S + x]) : null));
}

function sand(seed = 29) {
  return paint(field(seed, [[8, 0.35], [16, 0.25]], 0.7), hexes(0xcdb983, 0xd3c08b, 0xd8c690, 0xdccb95, 0xdccb95, 0xe0d09b, 0xe5d6a3, 0xebdcac));
}

function sandstoneSide() {
  const base = sand(31);
  const r = mulberry(33);
  return base.each((x, y) => {
    const c = base.get(x, y);
    if (y <= 2) return shade(c, 1.04);
    if (y === 3) return shade(c, 0.86);
    if (y === 11) return r() < 0.8 ? shade(c, 0.84) : c;
    if (y >= 12) return shade(c, 0.95);
    return c;
  });
}

function sandstoneTop() {
  return paint(field(35, [[4, 0.5], [8, 0.3]], 0.3), hexes(0xd2bf88, 0xd8c690, 0xdccb95, 0xe0cf9a, 0xe3d39f));
}

function gravel() {
  const t = paint(field(37, [[16, 0.5]], 0.6), hexes(0x5c5753, 0x655f5a, 0x6d6762));
  const r = mulberry(39);
  const stones = hexes(0x7b7470, 0x8a837e, 0x958e88, 0x9e9a96, 0x7f7468, 0xa59f99, 0x6f6a66);
  for (let i = 0; i < 26; i++) {
    const x = Math.floor(r() * S), y = Math.floor(r() * S);
    const c = stones[Math.floor(r() * stones.length)];
    const w = 1 + Math.floor(r() * 2), h = 1 + Math.floor(r() * 2);
    for (let dy = 0; dy < h; dy++) for (let dx = 0; dx < w; dx++) t.set(x + dx, y + dy, dy === 0 && dx === 0 ? shade(c, 1.12) : c);
    t.set(x + w, y + h, shade(c, 0.62));
  }
  return t;
}

function clay() {
  return paint(field(43, [[4, 0.6], [8, 0.3]], 0.25), hexes(0x969cab, 0x9ba1b0, 0xa0a6b5, 0xa4aab9, 0xa9afbd));
}

function snow() {
  return paint(field(47, [[8, 0.4], [16, 0.2]], 0.6), hexes(0xdde6ef, 0xe6edf4, 0xeef3f8, 0xf4f7fb, 0xf4f7fb, 0xf9fbfd, 0xffffff));
}

// ---------------------------------------------------------------- cobble & bricks

/** Rounded stones: a tileable Voronoi of `n` cells, mortar where two cells meet, light top-left / dark bottom-right. */
function cobble(seed: number, pal: RGB[], mortar: RGB) {
  const r = mulberry(seed);
  const pts = Array.from({ length: 11 }, () => [r() * S, r() * S, r()] as const);
  const n = field(seed + 1, [[16, 0.4]], 0.8);
  return new Tile().each((x, y) => {
    let d1 = 1e9, d2 = 1e9, best = 0, bx = 0, by = 0;
    pts.forEach(([px, py], i) => {
      for (const ox of [-S, 0, S]) for (const oy of [-S, 0, S]) {
        const dx = x + 0.5 - (px + ox), dy = y + 0.5 - (py + oy);
        const d = Math.hypot(dx, dy);
        if (d < d1) { d2 = d1; d1 = d; best = i; bx = dx; by = dy; } else if (d < d2) d2 = d;
      }
    });
    if (d2 - d1 < 1.1) return mortar;
    const tone = 0.3 + pts[best][2] * 0.4 + (-(bx + by) / Math.max(1, d1)) * 0.12 + (n[y * S + x] - 0.5) * 0.35 - (d2 - d1 < 2.2 ? 0.15 : 0);
    return pick(pal, Math.max(0, Math.min(0.999, tone)));
  });
}

function cobblestone() {
  return cobble(53, greys(0x5d, 0x6a, 0x76, 0x80, 0x88, 0x91, 0x9c, 0xa8), grey(0x4a));
}

function mossy() {
  const t = cobblestone();
  const f = field(57, [[4, 0.6], [16, 0.3]], 0.2);
  const moss = hexes(0x3f5a26, 0x4b6a2c, 0x587a33, 0x66893c);
  return t.each((x, y) => (f[y * S + x] > 0.62 ? pick(moss, (f[y * S + x] - 0.62) / 0.38) : t.get(x, y)));
}

function bricks() {
  const f = field(59, [[16, 0.5]], 0.7);
  const r = mulberry(61);
  const tones = Array.from({ length: 8 }, () => 0.85 + r() * 0.25);
  const base = rgb(0x9a5240), mortar = hexes(0x8f877f, 0xa29a92, 0xb0a9a2);
  return new Tile().each((x, y) => {
    const row = y >> 2, ry = y & 3;
    if (ry === 3) return pick(mortar, f[y * S + x]);
    const off = row & 1 ? 4 : 0;
    const bx = (x + off) & 7;
    if (bx === 7) return pick(mortar, f[y * S + x]);
    const id = (row * 2 + (((x + off) >> 3) & 1)) & 7;
    let c = shade(base, tones[id] * (0.92 + f[y * S + x] * 0.16));
    if (ry === 0) c = shade(c, 1.1);
    if (ry === 2) c = shade(c, 0.9);
    return c;
  });
}

function stoneBricks() {
  const f = field(63, [[8, 0.4], [16, 0.3]], 0.5);
  const pal = greys(0x76, 0x7c, 0x82, 0x88, 0x8e, 0x94);
  return new Tile().each((x, y) => {
    const row = y >> 3, ry = y & 7;
    const off = row & 1 ? 8 : 0;
    const bx = (x + off) & 15;
    if (ry === 7 || bx === 15) return grey(0x55);
    let c = pick(pal, f[y * S + x]);
    if (ry === 0 || bx === 0) c = shade(c, 1.14);
    else if (ry === 6 || bx === 14) c = shade(c, 0.82);
    return c;
  });
}

// ---------------------------------------------------------------- wood

interface Wood { bark: RGB[]; ring: RGB[]; planks: RGB[]; seam: RGB }

const OAK: Wood = {
  bark: hexes(0x4c3820, 0x58412a, 0x634a2e, 0x6d5233, 0x795c3a),
  ring: hexes(0x8d6f43, 0x9a7a4b, 0xa98754, 0xb5925d),
  planks: hexes(0x8c6e42, 0x97784a, 0xa1814f, 0xa98853, 0xb39058),
  seam: rgb(0x5f4a2c),
};
const SPRUCE: Wood = {
  bark: hexes(0x33230f, 0x3c2a15, 0x46321b, 0x503a21, 0x5a4227),
  ring: hexes(0x5e4426, 0x6b4e2e, 0x785935, 0x84633c),
  planks: hexes(0x5c4128, 0x664a2e, 0x6f5132, 0x785836, 0x80603b),
  seam: rgb(0x3d2a17),
};
const BIRCH: Wood = {
  bark: hexes(0xcfcac0, 0xd8d4ca, 0xe0dcd3, 0xe8e5dd),
  ring: hexes(0xb9a46e, 0xc5b17a, 0xd0bd86, 0xdac891),
  planks: hexes(0xb59f68, 0xbea870, 0xc7b178, 0xcfb97f, 0xd6c086),
  seam: rgb(0x8f7c4c),
};

function logSide(w: Wood, seed: number) {
  const col = field(seed, [[8, 0.6]], 0.6);
  const fine = field(seed + 1, [[16, 0.5]], 0.8);
  return new Tile().each((x, y) => {
    const v = col[x] * 0.7 + fine[y * S + x] * 0.3;
    const c = pick(w.bark, v);
    // vertical grooves
    return (x * 7 + (y >> 2) * 3) % 5 === 0 && fine[y * S + x] < 0.5 ? shade(c, 0.82) : c;
  });
}

function birchSide() {
  const t = logSide(BIRCH, 71);
  const r = mulberry(73);
  for (let i = 0; i < 9; i++) {
    const y = Math.floor(r() * S), x = Math.floor(r() * S), l = 2 + Math.floor(r() * 4);
    for (let k = 0; k < l; k++) t.set(x + k, y, k === 0 || k === l - 1 ? rgb(0x4a463f) : rgb(0x2e2b27));
    if (r() < 0.5) t.set(x + 1, y + 1, rgb(0x55514a));
  }
  return t;
}

function logTop(w: Wood, seed: number) {
  const f = field(seed, [[16, 0.5]], 0.5);
  return new Tile().each((x, y) => {
    if (x === 0 || y === 0 || x === 15 || y === 15) return pick(w.bark, f[y * S + x]);
    const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    const ring = Math.floor(d + (f[y * S + x] - 0.5) * 0.9);
    const c = pick(w.ring, ring % 2 ? 0.25 : 0.75);
    return shade(c, 0.94 + f[y * S + x] * 0.1);
  });
}

function planks(w: Wood, seed: number) {
  const f = field(seed, [[16, 0.4]], 0.6);
  const grain = field(seed + 2, [[4, 0.4], [16, 0.4]], 0.3);
  const r = mulberry(seed + 3);
  const joints = [Math.floor(r() * 12) + 2, Math.floor(r() * 12) + 2, Math.floor(r() * 12) + 2, Math.floor(r() * 12) + 2];
  const tone = [0.98, 1.04, 0.95, 1.01];
  return new Tile().each((x, y) => {
    const b = y >> 2, ry = y & 3;
    if (ry === 3) return w.seam;
    if (x === joints[b]) return shade(w.seam, 1.15);
    let c = pick(w.planks, grain[(b * 4 + 1) * S + x] * 0.6 + f[y * S + x] * 0.4);
    c = shade(c, tone[b]);
    if (ry === 0) c = shade(c, 1.06);
    return c;
  });
}

// ---------------------------------------------------------------- plants

/** cut-out leaves: grey clusters (tinted by the biome) with gaps */
function leaves(seed: number, pal: RGB[], holes = 0.16) {
  const f = field(seed, [[8, 0.5], [16, 0.4]], 0.4);
  const h = field(seed + 9, [[16, 0.6]], 0.6);
  return new Tile().each((x, y) => (h[y * S + x] < holes ? null : pick(pal, f[y * S + x])));
}

function spruceLeaves() {
  // dense short needles in two diagonal directions over a dark base, with a few gaps
  const pal = greys(0x4e, 0x5c, 0x6a, 0x78, 0x86, 0x94);
  const base = field(83, [[8, 0.4], [16, 0.4]], 0.5);
  const holes = field(85, [[16, 0.6]], 0.6);
  const t = new Tile().each((x, y) => (holes[y * S + x] < 0.1 ? null : pick(pal.slice(0, 3), base[y * S + x])));
  const r = mulberry(87);
  for (let i = 0; i < 46; i++) {
    const x = Math.floor(r() * S), y = Math.floor(r() * S), dir = r() < 0.5 ? 1 : -1, c = pal[3 + Math.floor(r() * 3)];
    for (let k = 0; k < 3; k++) t.set(x + k * dir, y + k, shade(c, 1 - k * 0.07));
  }
  return t;
}

/** grass blades growing from the bottom edge */
function shortGrass() {
  const r = mulberry(89);
  const t = new Tile().each(() => null);
  const xs = [1, 3, 4, 6, 8, 9, 11, 13, 14];
  for (const x0 of xs) {
    const h = 6 + Math.floor(r() * 9), lean = r() < 0.5 ? -1 : 1, leanAt = Math.floor(h * (0.4 + r() * 0.4));
    for (let k = 0; k < h; k++) {
      const x = x0 + (k > leanAt ? lean : 0), y = 15 - k;
      if (x < 0 || x > 15) continue;
      t.set(x, y, grey(Math.round(0xa0 + (k / h) * 0x40 + r() * 0x10)));
    }
  }
  return t;
}

function stemLeaves(t: Tile, stemTop: number) {
  const g1 = rgb(0x2f6b1f), g2 = rgb(0x3f8a2a), g3 = rgb(0x52a336);
  for (let y = stemTop; y <= 15; y++) t.set(7, y, y % 3 ? g2 : g1);
  t.set(6, 12, g2); t.set(5, 11, g3); t.set(4, 11, g2);
  t.set(8, 10, g2); t.set(9, 9, g3); t.set(10, 9, g2);
  t.set(6, 14, g1); t.set(8, 13, g1);
  return t;
}

/** small flower sprites, drawn from character maps (one character per pixel, "." = nothing) */
function sprite(rows: string[], colours: Record<string, number>, stemTop: number) {
  const t = stemLeaves(new Tile().each(() => null), stemTop);
  const top = 1 + Math.max(0, 7 - rows.length);
  rows.forEach((row, j) => {
    const left = Math.floor((S - row.length) / 2);
    [...row].forEach((ch, i) => { if (ch !== ".") t.set(left + i, top + j, rgb(colours[ch])); });
  });
  return t;
}

const poppy = () => sprite([".rRr.", "rRRRr", "RRkRR", "rRRRr", ".rdr."], { r: 0xd02a26, R: 0xe9473c, k: 0x2b1a12, d: 0x9e1a17 }, 7);
const dandelion = () => sprite([".yYy.", "yYYYy", "YYoYY", ".yYy."], { y: 0xe8c21a, Y: 0xffe043, o: 0xd99a0e }, 6);
const cornflower = () => sprite(["b.B.b", ".BbB.", "BBdBB", ".BbB.", "b.B.b"], { b: 0x3f5fd0, B: 0x6f8ff0, d: 0x23317a }, 7);
const daisy = () => sprite([".w.w.", "wWwWw", ".WyW.", "wWwWw", ".w.w."], { w: 0xdcdcdc, W: 0xffffff, y: 0xf0c020 }, 7);

function sugarCane() {
  const t = new Tile().each(() => null);
  const pal = greys(0x8a, 0xa2, 0xb8, 0xcc);
  for (const [x0, off] of [[3, 0], [8, 2], [12, 1]] as const) {
    for (let y = 0; y < S; y++) {
      const node = (y + off) % 5 === 0;
      t.set(x0, y, node ? pal[3] : pal[1]);
      t.set(x0 + 1, y, node ? pal[2] : pal[0]);
    }
    t.set(x0 - 1, (off * 3 + 4) % 16, pal[2]);
    t.set(x0 + 2, (off * 5 + 10) % 16, pal[2]);
  }
  return t;
}

function crop(ripe: boolean) {
  const r = mulberry(ripe ? 97 : 101);
  const t = new Tile().each(() => null);
  const stalk = ripe ? hexes(0x7a6a24, 0x8f7c2a, 0xa48e30) : hexes(0x3a7020, 0x4a8628, 0x5c9c30, 0x70b23a);
  const grain = hexes(0xb8922c, 0xcfa83a, 0xe0bd4c, 0xeccf66);
  for (const x0 of [1, 4, 7, 10, 13]) {
    const h = (ripe ? 12 : 6) + Math.floor(r() * 3);
    for (let k = 0; k < h; k++) t.set(x0, 15 - k, stalk[Math.min(stalk.length - 1, Math.floor((k / h) * stalk.length))]);
    if (!ripe) { t.set(x0 + 1, 15 - Math.floor(h / 2), stalk[2]); t.set(x0 - 1, 14 - Math.floor(h / 3), stalk[3]); continue; }
    // the ear: two rows of kernels on both sides of the stalk's top
    for (let k = h - 5; k < h; k++) {
      t.set(x0 - 1, 15 - k, grain[(k + x0) % 2 ? 1 : 2]);
      t.set(x0, 15 - k, grain[3]);
      t.set(x0 + 1, 15 - k, grain[(k + x0) % 2 ? 2 : 0]);
    }
    t.set(x0, 15 - h, grain[2]);
    t.set(x0 + 1, 14 - Math.floor(h / 2), stalk[1]);
  }
  return t;
}

function farmlandTop() {
  const f = field(103, [[8, 0.5], [16, 0.3]], 0.4);
  const pal = hexes(0x3a2414, 0x432a17, 0x4c311b, 0x55381f, 0x5e3f24);
  return new Tile().each((x, y) => {
    const c = pick(pal, f[y * S + x]);
    return y % 4 === 0 ? shade(c, 0.72) : y % 4 === 1 ? shade(c, 1.18) : c;
  });
}

function pathTop() {
  return paint(field(107, [[8, 0.4], [16, 0.3]], 0.6), hexes(0x7c6438, 0x866d3e, 0x8f7544, 0x977c48, 0x977c48, 0x9f844e, 0xa88c55));
}

function pathSide() {
  const d = dirt(109), top = pathTop();
  return d.each((x, y) => (y < 2 ? top.get(x, y) : y === 2 ? shade(top.get(x, y), 0.8) : d.get(x, y)));
}

function water() {
  const f = field(113, [[2, 0.5], [4, 0.4]], 0.25, (x, y) => Math.sin(((x + y * 2) / 16) * Math.PI * 2) * 0.25);
  return paint(f, greys(0xb4, 0xba, 0xc0, 0xc6, 0xcc, 0xd4, 0xdc));
}

function glass() {
  const frame = rgb(0xc8e3ea), dark = rgb(0x9fc3cc), streak = rgb(0xf2fbff);
  const t = new Tile().each((x, y) => (x === 0 || y === 0 ? frame : x === 15 || y === 15 ? dark : null));
  for (const [x, y] of [[3, 4], [4, 3], [5, 2], [4, 5], [5, 4], [6, 3], [10, 11], [11, 10], [12, 9]]) t.set(x, y, streak);
  return t;
}

function hayTop() {
  const f = field(127, [[8, 0.4], [16, 0.4]], 0.6);
  const pal = hexes(0x9a7a18, 0xae8c1e, 0xc29d26, 0xd2ad30, 0xdcb93c);
  return new Tile().each((x, y) => {
    const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    const c = pick(pal, f[y * S + x]);
    return Math.floor(d) % 3 === 0 ? shade(c, 0.85) : c;
  });
}

function haySide() {
  const f = field(131, [[16, 0.5]], 0.7);
  const pal = hexes(0xa8861c, 0xb99522, 0xc8a32a, 0xd6b134, 0xdfbc40);
  const tie = hexes(0x6b4a14, 0x7d5a1a);
  return new Tile().each((x, y) => {
    if (y === 3 || y === 12) return pick(tie, f[y * S + x]);
    const c = pick(pal, f[y * S + x] * 0.5 + ((x * 5) % 7) / 14);
    return x % 3 === 0 ? shade(c, 0.9) : c;
  });
}

function pumpkinSide() {
  const f = field(137, [[16, 0.5]], 0.5);
  const pal = hexes(0xb8601a, 0xc96c1c, 0xd67820, 0xe08424);
  return new Tile().each((x, y) => {
    const rib = x === 0 || x === 5 || x === 10;
    const c = pick(pal, f[y * S + x]);
    if (rib) return shade(c, 0.72);
    if (x === 1 || x === 6 || x === 11) return shade(c, 1.08);
    return y === 0 || y === 15 ? shade(c, 0.85) : c;
  });
}

function pumpkinTop() {
  const side = pumpkinSide();
  return side.each((x, y) => {
    const d = Math.hypot(x - 7.5, y - 7.5);
    if (d < 1.6) return rgb(0x4d5a1e);
    if (d < 2.6) return rgb(0x6a7224);
    return shade(side.get(x, y), 0.9 + (d / 12) * 0.1);
  });
}

// ---------------------------------------------------------------- the texture set

const TEXTURES: Record<string, () => Tile> = {
  stone, granite, diorite, andesite, dirt, sand, gravel, clay, snow, cobblestone, bricks,
  coal_ore: () => ore(141, hexes(0x161616, 0x262626, 0x3a3a3a)),
  iron_ore: () => ore(143, hexes(0xb98a6a, 0xd6aa8a, 0xe8c4a8)),
  copper_ore: () => ore(147, hexes(0x9c4f2a, 0xd27845, 0x6fa98b)),
  grass_block_top: grassTop,
  grass_block_side: () => dirt(7),
  grass_block_side_overlay: grassSideOverlay,
  grass_block_snow: () => { const s = snow(), d = dirt(7); return d.each((x, y) => (y < 3 + ((x * 7) % 3 === 0 ? 1 : 0) ? s.get(x, y) : d.get(x, y))); },
  sandstone: sandstoneSide,
  sandstone_top: sandstoneTop,
  sandstone_bottom: () => paint(field(149, [[8, 0.5]], 0.4), hexes(0xc8b47c, 0xcfbb84, 0xd5c18a, 0xdac790)),
  mossy_cobblestone: mossy,
  stone_bricks: stoneBricks,
  oak_log: () => logSide(OAK, 151),
  oak_log_top: () => logTop(OAK, 153),
  spruce_log: () => logSide(SPRUCE, 157),
  spruce_log_top: () => logTop(SPRUCE, 159),
  birch_log: birchSide,
  birch_log_top: () => logTop(BIRCH, 163),
  oak_planks: () => planks(OAK, 167),
  spruce_planks: () => planks(SPRUCE, 173),
  birch_planks: () => planks(BIRCH, 179),
  oak_leaves: () => leaves(181, greys(0x60, 0x70, 0x80, 0x90, 0xa0, 0xb0)),
  birch_leaves: () => leaves(191, greys(0x74, 0x84, 0x94, 0xa4, 0xb4, 0xc4), 0.2),
  spruce_leaves: spruceLeaves,
  short_grass: shortGrass,
  poppy, dandelion, cornflower, oxeye_daisy: daisy,
  sugar_cane: sugarCane,
  wheat_stage3: () => crop(false),
  wheat_stage7: () => crop(true),
  farmland_moist: farmlandTop,
  dirt_path_top: pathTop,
  dirt_path_side: pathSide,
  water_still: water,
  water_flow: water,
  lava_still: () => paint(field(117, [[4, 0.6], [8, 0.3]], 0.2), hexes(0xb8340c, 0xd04a10, 0xe46414, 0xf08a1e, 0xfab432)),
  lava_flow: () => paint(field(119, [[4, 0.6], [8, 0.3]], 0.2), hexes(0xb8340c, 0xd04a10, 0xe46414, 0xf08a1e, 0xfab432)),
  glass,
  hay_block_top: hayTop,
  hay_block_side: haySide,
  pumpkin_side: pumpkinSide,
  pumpkin_top: pumpkinTop,
};

// ---------------------------------------------------------------- models (vanilla format)

type Faces = Record<string, { texture: string; cullface?: string; tintindex?: number; uv?: number[] }>;
const tex = (name: string) => `minecraft:block/${name}`;
const DIR6 = ["down", "up", "north", "south", "west", "east"] as const;

function cubeFaces(tx: (d: (typeof DIR6)[number]) => string, tint?: (d: string) => boolean): Faces {
  const f: Faces = {};
  for (const d of DIR6) f[d] = { texture: tx(d), cullface: d, ...(tint?.(d) ? { tintindex: 0 } : {}) };
  return f;
}

const MODELS: Record<string, unknown> = {
  "mcplanner:block/cube": { elements: [{ from: [0, 0, 0], to: [16, 16, 16], faces: cubeFaces((d) => `#${d}`) }] },
  "mcplanner:block/cube_all": { parent: "mcplanner:block/cube", textures: { down: "#all", up: "#all", north: "#all", south: "#all", west: "#all", east: "#all", particle: "#all" } },
  "mcplanner:block/cube_column": { parent: "mcplanner:block/cube", textures: { down: "#end", up: "#end", north: "#side", south: "#side", west: "#side", east: "#side", particle: "#side" } },
  "mcplanner:block/cube_bottom_top": { parent: "mcplanner:block/cube", textures: { down: "#bottom", up: "#top", north: "#side", south: "#side", west: "#side", east: "#side", particle: "#side" } },
  "mcplanner:block/leaves": { elements: [{ from: [0, 0, 0], to: [16, 16, 16], faces: cubeFaces(() => "#all", () => true) }] },
  "mcplanner:block/grass_block": {
    textures: { particle: tex("dirt"), bottom: tex("dirt"), top: tex("grass_block_top"), side: tex("grass_block_side"), overlay: tex("grass_block_side_overlay") },
    elements: [
      { from: [0, 0, 0], to: [16, 16, 16], faces: cubeFaces((d) => (d === "down" ? "#bottom" : d === "up" ? "#top" : "#side"), (d) => d === "up") },
      { from: [0, 0, 0], to: [16, 16, 16], faces: Object.fromEntries(["north", "south", "west", "east"].map((d) => [d, { texture: "#overlay", cullface: d, tintindex: 0 }])) },
    ],
  },
  "mcplanner:block/grass_block_snow": { parent: "mcplanner:block/cube_bottom_top", textures: { bottom: tex("dirt"), top: tex("snow"), side: tex("grass_block_snow") } },
  "mcplanner:block/cross": {
    ambientocclusion: false,
    elements: [
      { from: [0.8, 0, 8], to: [15.2, 16, 8], rotation: { origin: [8, 8, 8], axis: "y", angle: 45, rescale: true }, shade: false, faces: { north: { texture: "#cross" }, south: { texture: "#cross" } } },
      { from: [8, 0, 0.8], to: [8, 16, 15.2], rotation: { origin: [8, 8, 8], axis: "y", angle: 45, rescale: true }, shade: false, faces: { west: { texture: "#cross" }, east: { texture: "#cross" } } },
    ],
  },
  "mcplanner:block/tinted_cross": {
    ambientocclusion: false,
    elements: [
      { from: [0.8, 0, 8], to: [15.2, 16, 8], rotation: { origin: [8, 8, 8], axis: "y", angle: 45, rescale: true }, shade: false, faces: { north: { texture: "#cross", tintindex: 0 }, south: { texture: "#cross", tintindex: 0 } } },
      { from: [8, 0, 0.8], to: [8, 16, 15.2], rotation: { origin: [8, 8, 8], axis: "y", angle: 45, rescale: true }, shade: false, faces: { west: { texture: "#cross", tintindex: 0 }, east: { texture: "#cross", tintindex: 0 } } },
    ],
  },
  "mcplanner:block/crop": {
    ambientocclusion: false,
    elements: [
      { from: [4, -1, 0], to: [4, 15, 16], shade: false, faces: { west: { texture: "#crop" }, east: { texture: "#crop" } } },
      { from: [12, -1, 0], to: [12, 15, 16], shade: false, faces: { west: { texture: "#crop" }, east: { texture: "#crop" } } },
      { from: [0, -1, 4], to: [16, 15, 4], shade: false, faces: { north: { texture: "#crop" }, south: { texture: "#crop" } } },
      { from: [0, -1, 12], to: [16, 15, 12], shade: false, faces: { north: { texture: "#crop" }, south: { texture: "#crop" } } },
    ],
  },
  "mcplanner:block/slab_height": {
    elements: [{ from: [0, 0, 0], to: [16, 15, 16], faces: { down: { texture: "#bottom", cullface: "down" }, up: { texture: "#top" }, north: { texture: "#side", cullface: "north" }, south: { texture: "#side", cullface: "south" }, west: { texture: "#side", cullface: "west" }, east: { texture: "#side", cullface: "east" } } }],
  },
  "mcplanner:block/snow_layer": {
    textures: { particle: tex("snow"), texture: tex("snow") },
    elements: [{ from: [0, 0, 0], to: [16, 2, 16], faces: { down: { texture: "#texture", cullface: "down" }, up: { texture: "#texture" }, north: { texture: "#texture", cullface: "north" }, south: { texture: "#texture", cullface: "south" }, west: { texture: "#texture", cullface: "west" }, east: { texture: "#texture", cullface: "east" } } }],
  },
  // stairs facing east (the variants turn them), bottom half
  "mcplanner:block/stairs": {
    elements: [
      { from: [0, 0, 0], to: [16, 8, 16], faces: { down: { texture: "#bottom", cullface: "down" }, up: { texture: "#top" }, north: { texture: "#side", cullface: "north" }, south: { texture: "#side", cullface: "south" }, west: { texture: "#side", cullface: "west" }, east: { texture: "#side", cullface: "east" } } },
      { from: [8, 8, 0], to: [16, 16, 16], faces: { up: { texture: "#top", cullface: "up" }, north: { texture: "#side", cullface: "north" }, south: { texture: "#side", cullface: "south" }, west: { texture: "#side" }, east: { texture: "#side", cullface: "east" } } },
    ],
  },
  "mcplanner:block/fence_post": {
    elements: [{ from: [6, 0, 6], to: [10, 16, 10], faces: { down: { texture: "#texture", cullface: "down" }, up: { texture: "#texture", cullface: "up" }, north: { texture: "#texture" }, south: { texture: "#texture" }, west: { texture: "#texture" }, east: { texture: "#texture" } } }],
  },
  "mcplanner:block/fence_side": {
    elements: [
      { from: [7, 12, 0], to: [9, 15, 9], faces: { down: { texture: "#texture" }, up: { texture: "#texture" }, north: { texture: "#texture", cullface: "north" }, west: { texture: "#texture" }, east: { texture: "#texture" } } },
      { from: [7, 6, 0], to: [9, 9, 9], faces: { down: { texture: "#texture" }, up: { texture: "#texture" }, north: { texture: "#texture", cullface: "north" }, west: { texture: "#texture" }, east: { texture: "#texture" } } },
    ],
  },
  "mcplanner:block/water": { textures: { particle: tex("water_still") } },
};

/** a model with a parent and its textures */
function child(id: string, parent: string, textures: Record<string, string>) {
  MODELS[`mcplanner:block/${id}`] = { parent: `mcplanner:block/${parent}`, textures: { particle: Object.values(textures)[0], ...textures } };
  return `mcplanner:block/${id}`;
}

const BLOCKSTATES: Record<string, unknown> = {};
const one = (block: string, model: string) => (BLOCKSTATES[`minecraft:${block}`] = { variants: { "": { model } } });
const all = (block: string, texture = block) => one(block, child(block, "cube_all", { all: tex(texture) }));
const column = (block: string, side: string, end: string) => {
  const m = child(block, "cube_column", { side: tex(side), end: tex(end) });
  BLOCKSTATES[`minecraft:${block}`] = { variants: { "axis=y": { model: m }, "axis=z": { model: m, x: 90 }, "axis=x": { model: m, x: 90, y: 90 } } };
};

for (const b of ["stone", "granite", "diorite", "andesite", "dirt", "sand", "gravel", "clay", "cobblestone", "mossy_cobblestone", "bricks", "stone_bricks", "coal_ore", "iron_ore", "copper_ore", "oak_planks", "spruce_planks", "birch_planks", "glass"]) all(b);
all("snow_block", "snow");
BLOCKSTATES["minecraft:grass_block"] = { variants: { "snowy=false": { model: "mcplanner:block/grass_block" }, "snowy=true": { model: "mcplanner:block/grass_block_snow" } } };
one("sandstone", child("sandstone", "cube_bottom_top", { top: tex("sandstone_top"), bottom: tex("sandstone_bottom"), side: tex("sandstone") }));
column("oak_log", "oak_log", "oak_log_top");
column("spruce_log", "spruce_log", "spruce_log_top");
column("birch_log", "birch_log", "birch_log_top");
column("hay_block", "hay_block_side", "hay_block_top");
one("pumpkin", child("pumpkin", "cube_column", { side: tex("pumpkin_side"), end: tex("pumpkin_top") }));
for (const b of ["oak_leaves", "birch_leaves", "spruce_leaves"]) one(b, child(b, "leaves", { all: tex(b) }));
one("short_grass", child("short_grass", "tinted_cross", { cross: tex("short_grass") }));
one("sugar_cane", child("sugar_cane", "tinted_cross", { cross: tex("sugar_cane") }));
for (const b of ["poppy", "dandelion", "cornflower", "oxeye_daisy"]) one(b, child(b, "cross", { cross: tex(b) }));
one("farmland", child("farmland", "slab_height", { top: tex("farmland_moist"), side: tex("dirt"), bottom: tex("dirt") }));
one("dirt_path", child("dirt_path", "slab_height", { top: tex("dirt_path_top"), side: tex("dirt_path_side"), bottom: tex("dirt") }));
one("snow", "mcplanner:block/snow_layer");
one("water", "mcplanner:block/water");
{
  const young = child("wheat_stage3", "crop", { crop: tex("wheat_stage3") }), ripe = child("wheat_stage7", "crop", { crop: tex("wheat_stage7") });
  BLOCKSTATES["minecraft:wheat"] = { variants: Object.fromEntries(Array.from({ length: 8 }, (_, a) => [`age=${a}`, { model: a >= 6 ? ripe : young }])) };
}
for (const [block, texture] of [["oak_stairs", "oak_planks"], ["spruce_stairs", "spruce_planks"], ["cobblestone_stairs", "cobblestone"], ["stone_brick_stairs", "stone_bricks"]] as const) {
  const m = child(block, "stairs", { top: tex(texture), bottom: tex(texture), side: tex(texture) });
  const turn: Record<string, number> = { east: 0, south: 90, west: 180, north: 270 };
  const variants: Record<string, unknown> = {};
  for (const [facing, y] of Object.entries(turn)) {
    variants[`facing=${facing},half=bottom,shape=straight`] = { model: m, y, uvlock: true };
    variants[`facing=${facing},half=top,shape=straight`] = { model: m, x: 180, y, uvlock: true };
  }
  BLOCKSTATES[`minecraft:${block}`] = { variants };
}
for (const [block, texture] of [["oak_fence", "oak_planks"], ["spruce_fence", "spruce_planks"]] as const) {
  const post = child(`${block}_post`, "fence_post", { texture: tex(texture) }), side = child(`${block}_side`, "fence_side", { texture: tex(texture) });
  BLOCKSTATES[`minecraft:${block}`] = {
    multipart: [
      { apply: { model: post } },
      { when: { north: "true" }, apply: { model: side, uvlock: true } },
      { when: { east: "true" }, apply: { model: side, y: 90, uvlock: true } },
      { when: { south: "true" }, apply: { model: side, y: 180, uvlock: true } },
      { when: { west: "true" }, apply: { model: side, y: 270, uvlock: true } },
    ],
  };
}

// ---------------------------------------------------------------- biome colours

/**
 * Grass / foliage colour maps (256 × 256, laid out like the game's: x = 1 − temperature, y = 1 − rain × temperature),
 * blended from four corner colours of our own choosing.
 */
function colormap(hotWet: number, hotDry: number, cold: number, mild: number) {
  const px = new Uint8Array(256 * 256 * 4);
  const [a, b, c, d] = [hotWet, hotDry, cold, mild].map(rgb);
  for (let y = 0; y < 256; y++)
    for (let x = 0; x < 256; x++) {
      const t = 1 - x / 255, rain = 1 - y / 255; // rain = downfall × temperature
      const wet = t > 0 ? Math.min(1, rain / t) : 0;
      const warm = mixc(b, a, wet), cool = mixc(c, d, wet * 0.5 + 0.25);
      const col = mixc(cool, warm, Math.pow(t, 0.9));
      px.set([col[0], col[1], col[2], 255], (y * 256 + x) * 4);
    }
  return px;
}

let cached: AssetBundle | null = null;

export const DEMO_BLOCKS_LABEL = "MC Planner's built-in blocks";

/** The built-in blocks as an asset bundle (built once, ~50 textures, a few milliseconds). */
export function demoBlocksBundle(): AssetBundle {
  if (cached) return cached;
  const images = new Map<string, Rgba>();
  for (const [name, draw] of Object.entries(TEXTURES)) images.set(tex(name), draw().rgba());
  cached = {
    label: DEMO_BLOCKS_LABEL,
    blockstates: BLOCKSTATES,
    models: MODELS,
    textures: textureArrayFrom(images),
    colormaps: {
      grass: colormap(0x4fb831, 0xbcb158, 0x7fae8f, 0x86b85a),
      foliage: colormap(0x2fa612, 0xa69a2c, 0x5f9a7a, 0x5ba83a),
      dryFoliage: null,
    },
  };
  return cached;
}

/** names of every block the built-in set can draw (for tests) */
export function demoBlockNames() {
  return Object.keys(BLOCKSTATES);
}
